import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type {
  GetEncounterVitalsResponse,
  PatientVital,
} from '@/types/vitals-api';

/**
 * GET /api/Patients/GetVitalsDetailsByEncounter?PatientId={patientId}&EncounterId={encounterId}
 *
 * Mirrors Flutter `GetEncounterVitalsEvent` → the patient encounter
 * vitals fetch used by `vitals_tab.dart` (line 203).
 *
 * Returns a bare array of `PatientVital` rows (curl-verified 2026-05-15).
 * No envelope — the response IS the array.
 *
 * If the encounter has never recorded vitals, the backend returns `[]`,
 * NOT 404. Consumers should treat empty array as "no vitals yet" and
 * render an empty grid using the catalog template.
 *
 * AbortSignal forwarded per project rule (`feedback_signal_forwarding`).
 *
 * @see {@link ../components/view-appointment-panel/vitals/vital-catalog.ts}
 */

export interface UseEncounterVitalsParams {
  patientId: string | null | undefined;
  encounterId: string | null | undefined;
  enabled?: boolean;
}

export function useEncounterVitals(
  params: UseEncounterVitalsParams,
): UseQueryResult<PatientVital[]> {
  const { patientId, encounterId, enabled = true } = params;

  return useQuery<PatientVital[]>({
    queryKey: ['encounter-vitals', encounterId, patientId],
    enabled: enabled && !!patientId && !!encounterId,
    queryFn: ({ signal }) =>
      get<GetEncounterVitalsResponse>(
        `/api/Patients/GetVitalsDetailsByEncounter?PatientId=${encodeURIComponent(
          patientId as string,
        )}&EncounterId=${encodeURIComponent(encounterId as string)}`,
        { signal },
      ),
  });
}
